import { useEffect } from 'react';
import { t } from '@lingui/macro';

import { Box, Button, Flex, Image, Text } from '@rover/kibble/core';
import { Props as BoxProps } from '@rover/kibble/core/Box/Box';
import { SrcsetArray } from '@rover/kibble/core/Image';
import type { Image as ImageType } from '@rover/rsdk/src/apiClient/latest/model/image';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { buildSrcset } from '../../../utils/image';

import { PhotoGalleryLoadedEvent } from './AnalyticsEvents';
import { DEFAULT_IMAGE_OFFSET, GALLERY_PREVIEW_IMAGE_COUNT, SMALL_IMAGE_OFFSET } from './constants';

const ONE_IMAGE_WIDTHS: SrcsetArray = ['450', '718', '738'];
const IMAGE_WIDTHS: SrcsetArray = ['450', '360', '370'];
const SMALL_IMAGE_WIDTHS: SrcsetArray = ['170', '170', '360'];

const EXPERIMENT_ONE_IMAGE_WIDTHS: SrcsetArray = ['450', '738', '738'];
const EXPERIMENT_IMAGE_WIDTHS: SrcsetArray = ['450', '360', '360'];
const EXPERIMENT_WIDE_SMALL_IMAGE_WIDTHS: SrcsetArray = ['450', '450', '450'];

const srcsetFallbackUrl = (srcset: SrcsetArray): string => {
  const urls = srcset.filter((url): url is string => url !== null);
  return urls[urls.length - 1] ?? '';
};

type Props = {
  images: ImageType[];
  onImageClick: (index: number) => void;
  onViewAllClick?: () => void;
  count: number;
  trainerProfileSpacing?: boolean;
  hideOverlay?: boolean;
  isInSitterGalleryCropFixExperiment?: boolean;
  showMobileViewAllButton?: boolean;
  smallImagesGridWidth?: string[];
};

type HeaderImagesProps = { firstImage: SrcsetArray; smallImages: SrcsetArray[] };

const AnimatedBox = ({
  children,
  ...props
}: BoxProps & {
  onClick: () => void;
  isInSitterGalleryCropFixExperiment: boolean;
}): JSX.Element => {
  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      props.onClick();
    }
  };
  const sx = {
    overflow: props.isInSitterGalleryCropFixExperiment ? 'hidden' : 'visible',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      cursor: 'pointer',
      transform: 'scale(1.03)',
    },
    ...props.sx,
  };
  return (
    <Box
      position={props.isInSitterGalleryCropFixExperiment ? 'relative' : 'static'}
      {...props}
      webProps={{
        onKeyDown,
        tabIndex: 0,
      }}
      role="button"
      sx={sx}
    >
      {children}
    </Box>
  );
};

type OverlayProps = {
  total: number;
  shouldShow?: boolean;
  isInSitterGalleryCropFixExperiment?: boolean;
};

const Overlay = ({
  total,
  shouldShow = true,
  isInSitterGalleryCropFixExperiment = false,
}: OverlayProps): JSX.Element => (
  <Box
    position="absolute"
    width="100%"
    height="100%"
    top="0"
    py={isInSitterGalleryCropFixExperiment ? '0x' : '1x'}
    pr={isInSitterGalleryCropFixExperiment ? '0x' : '2x'}
    sx={{
      transition: 'all 0.2s ease-in-out',
      opacity: shouldShow ? 1 : 0,
    }}
  >
    <Box
      display="flex"
      background="overlay"
      height="100%"
      borderRadius="primary"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
    >
      <Text textColor="primary_inverse" size="200" fontWeight="semibold">
        {total}
      </Text>
      <Text textColor="primary_inverse" size="200" fontWeight="semibold">
        {t`Photos`}
      </Text>
      <Text textColor="primary_inverse" size="100" fontWeight="regular">
        {t`View All`}
      </Text>
    </Box>
  </Box>
);

const PhotoGallery = ({
  images,
  onImageClick,
  onViewAllClick,
  count,
  trainerProfileSpacing,
  hideOverlay = false,
  isInSitterGalleryCropFixExperiment = false,
  showMobileViewAllButton = false,
  smallImagesGridWidth,
}: Props): JSX.Element => {
  const shouldUseWideSmallImage = (imagesLength, index): boolean => {
    return imagesLength <= 2 || (imagesLength === 3 && index === 2);
  };

  const getHeaderImages = (useCropFix?: boolean): HeaderImagesProps => {
    if (useCropFix || isInSitterGalleryCropFixExperiment) {
      const first: SrcsetArray =
        images.length === 1
          ? [images[0].medium, images[0].galleryMain, images[0].galleryMain]
          : [images[0].medium, images[0].galleryThumb, images[0].galleryThumb];

      const experimentSmallImages = images.slice(1, GALLERY_PREVIEW_IMAGE_COUNT);
      const rest = experimentSmallImages.map((image, index) => {
        const wideSmallImage = shouldUseWideSmallImage(experimentSmallImages.length, index);
        if (wideSmallImage) {
          return [image.medium, image.medium, image.medium] as SrcsetArray;
        }
        return [image.gallerySmall, image.gallerySmall, image.gallerySmallWide] as SrcsetArray;
      });

      return { firstImage: first, smallImages: rest };
    }

    const url = images[0].small.split('?')[0];

    const firstImageWidths = images.length === 1 ? ONE_IMAGE_WIDTHS : IMAGE_WIDTHS;

    const sizes = firstImageWidths.map((w) => ({
      width: w,
      height: trainerProfileSpacing ? '220' : '280',
    }));
    const first = buildSrcset(url, sizes);

    const smallImages = images.slice(1, GALLERY_PREVIEW_IMAGE_COUNT);
    const SMALL_IMAGE_SIZES = SMALL_IMAGE_WIDTHS.map((size) => {
      return {
        width: size,
        height: '136',
      };
    });

    const rest = smallImages.map((image) => {
      const imgUrl = image.small.split('?')[0];
      return buildSrcset(imgUrl, SMALL_IMAGE_SIZES);
    });
    return { firstImage: first, smallImages: rest };
  };

  const { firstImage, smallImages } = getHeaderImages();
  const { firstImage: firstImageWithCropFix } = getHeaderImages(true);

  useEffect(() => {
    emitAnalyticsEvent(new PhotoGalleryLoadedEvent());
  }, []);

  const getAriaLabel = (index: number): string => {
    const imageNumber = index + 1;
    const totalNumberOfImages = images.length;
    return t`Image ${imageNumber} of ${totalNumberOfImages}, view enlarged`;
  };

  const getAlt = (index: number): string => {
    const imageNumber = index + 1;
    const totalNumberOfImages = images.length;
    return t`Image ${imageNumber} of ${totalNumberOfImages}`;
  };

  const getSizesForIndex = (index: number, total: number): { width: string; height: string } => {
    switch (total) {
      case 1:
        return { width: '100%', height: '100%' };
      case 2:
        return { width: '100%', height: '50%' };
      case 3:
        return { width: index === 2 ? '100%' : '50%', height: '50%' };
      default:
        return { width: '50%', height: '50%' };
    }
  };

  const overlay = images.length > GALLERY_PREVIEW_IMAGE_COUNT;
  const firstImageWidths = images.length === 1 ? ONE_IMAGE_WIDTHS : IMAGE_WIDTHS;

  const experimentFirstImageWidths =
    images.length === 1 ? EXPERIMENT_ONE_IMAGE_WIDTHS : EXPERIMENT_IMAGE_WIDTHS;
  const effectiveFirstImageWidths = isInSitterGalleryCropFixExperiment
    ? experimentFirstImageWidths
    : firstImageWidths;

  const getExperimentSmallImageWidths = (index: number): SrcsetArray => {
    const wideSmallImage = shouldUseWideSmallImage(smallImages.length, index);
    if (wideSmallImage) {
      return EXPERIMENT_WIDE_SMALL_IMAGE_WIDTHS;
    }
    return SMALL_IMAGE_WIDTHS;
  };

  const getImageHeight = (): string => {
    if (trainerProfileSpacing) {
      return '220px';
    }
    if (isInSitterGalleryCropFixExperiment) {
      return '100%';
    }
    return '280px';
  };

  const getImageLayout = (index): string => {
    switch (smallImages.length) {
      case 1:
      case 2:
        return '1 / span 2';
      case 3:
        if (index === 2) {
          return '1 / span 2';
        }
        return 'unset';
      default:
        return 'unset';
    }
  };

  const showMobileCta = showMobileViewAllButton && !!onViewAllClick && count > 1;

  return (
    <Flex
      flexDirection="row"
      position={showMobileCta ? 'relative' : undefined}
      height={
        isInSitterGalleryCropFixExperiment
          ? ['inherit', 'inherit', 'unset']
          : ['inherit', 'unset', 'unset']
      }
      width="100%"
      data-testid="photo-gallery"
      borderRadius={trainerProfileSpacing ? undefined : 'primary'}
    >
      <AnimatedBox
        display={['block', 'none']}
        flex="1"
        onClick={() => onImageClick(DEFAULT_IMAGE_OFFSET)}
        borderRadius={trainerProfileSpacing ? undefined : 'primary'}
        isInSitterGalleryCropFixExperiment
        paddingRight="0x"
      >
        <Image
          src={srcsetFallbackUrl(firstImageWithCropFix)}
          height={trainerProfileSpacing ? '220px' : '100%'}
          width="100%"
          marginTop="0x"
          webProps={{
            src: firstImageWithCropFix,
            srcsetWidths: experimentFirstImageWidths,
            sizes: experimentFirstImageWidths.map((size) => `${size}px`),
            eager: true,
          }}
          resizeMethod="cover"
          description={getAlt(0)}
          aria-label={getAriaLabel(0)}
          borderRadius={trainerProfileSpacing ? undefined : 'primary'}
        />
      </AnimatedBox>
      <AnimatedBox
        display={['none', 'block']}
        flex="1"
        onClick={() => onImageClick(DEFAULT_IMAGE_OFFSET)}
        borderRadius={trainerProfileSpacing ? undefined : 'primary'}
        isInSitterGalleryCropFixExperiment={isInSitterGalleryCropFixExperiment}
        paddingRight={
          isInSitterGalleryCropFixExperiment ? ['0x', smallImages.length === 0 ? '0x' : '1x'] : '0x'
        }
      >
        <Image
          src={srcsetFallbackUrl(firstImage)}
          height={getImageHeight()}
          width="100%"
          marginTop={isInSitterGalleryCropFixExperiment ? '0x' : '1x'}
          webProps={{
            src: firstImage,
            srcsetWidths: effectiveFirstImageWidths,
            sizes: effectiveFirstImageWidths.map((size) => `${size}px`),
            eager: true,
          }}
          resizeMethod="cover"
          description={getAlt(0)}
          aria-label={getAriaLabel(0)}
          borderRadius={trainerProfileSpacing ? undefined : 'primary'}
        />
      </AnimatedBox>
      {showMobileCta && (
        <Box position="absolute" bottom="2x" right="2x" display={['block', 'none']} zIndex="1">
          <Button variant="default" size="small" onPress={onViewAllClick}>
            {t`View all ${count}`}
          </Button>
        </Box>
      )}
      {smallImages.length > 0 && (
        <Flex
          display={['none', isInSitterGalleryCropFixExperiment ? 'grid' : 'flex']}
          width={
            smallImagesGridWidth ?? [
              isInSitterGalleryCropFixExperiment ? '0%' : '100%',
              '50%',
              '66%',
              '50%',
            ]
          }
          flexWrap="wrap"
          height={isInSitterGalleryCropFixExperiment ? '100%' : '288px'}
          ml={isInSitterGalleryCropFixExperiment ? '1x' : '0x'}
          mt="0x"
          mb="0x"
          sx={
            isInSitterGalleryCropFixExperiment
              ? {
                  gap: '8px 8px',
                  'grid-template-columns': '1fr 1fr',
                  'grid-auto-rows': '1fr',
                }
              : {}
          }
        >
          {smallImages.map((image, index) => {
            const addOverlay =
              overlay && index === GALLERY_PREVIEW_IMAGE_COUNT - SMALL_IMAGE_OFFSET;
            const effectiveSmallWidths = isInSitterGalleryCropFixExperiment
              ? getExperimentSmallImageWidths(index)
              : SMALL_IMAGE_WIDTHS;
            const smallImageAriaLabel =
              addOverlay && onViewAllClick ? t`View all ${count}` : getAriaLabel(index + 1);
            return (
              <AnimatedBox
                width={
                  isInSitterGalleryCropFixExperiment
                    ? 'unset'
                    : getSizesForIndex(index, smallImages.length).width
                }
                height={
                  isInSitterGalleryCropFixExperiment
                    ? 'unset'
                    : getSizesForIndex(index, smallImages.length).height
                }
                sx={
                  isInSitterGalleryCropFixExperiment
                    ? {
                        gridColumn: getImageLayout(index),
                      }
                    : {}
                }
                p={isInSitterGalleryCropFixExperiment ? '0x' : '1x'}
                key={index}
                position="relative"
                onClick={() =>
                  addOverlay && onViewAllClick
                    ? onViewAllClick()
                    : onImageClick(index + SMALL_IMAGE_OFFSET)
                }
                isInSitterGalleryCropFixExperiment={isInSitterGalleryCropFixExperiment}
                webProps={{
                  'aria-label': smallImageAriaLabel,
                }}
              >
                <Image
                  src={srcsetFallbackUrl(image)}
                  height="100%"
                  width="100%"
                  webProps={{
                    src: image,
                    srcsetWidths: effectiveSmallWidths,
                    sizes: effectiveSmallWidths.map((size) => `${size}px`),
                    eager: true,
                  }}
                  description={getAlt(index + 1)}
                  borderRadius="primary"
                />
                {addOverlay && (
                  <Overlay
                    total={count}
                    shouldShow={!hideOverlay}
                    isInSitterGalleryCropFixExperiment={isInSitterGalleryCropFixExperiment}
                  />
                )}
              </AnimatedBox>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};

export default PhotoGallery;
